import type { BookingStatus, TripExperiencePhase } from '../types/booking';

/**
 * How a trip is described to a customer.
 *
 * Ported verbatim from the user app's home screen. These strings are the words
 * a customer already associates with a state — if the web invented its own
 * ("In transit" for the app's "En route") the same delivery would be described
 * two different ways on two screens, and the customer would reasonably wonder
 * whether they were looking at the same thing.
 */

export function phaseToLabel(phase: TripExperiencePhase): string {
  switch (phase) {
    case 'DRIVER_APPROACHING_PICKUP':
    case 'DRIVER_ON_ROUTE_TO_PICKUP':
      return 'Driver on the way to pickup';
    case 'DRIVER_AT_PICKUP':
      return 'Driver at pickup';
    case 'DRIVER_HEADING_TO_DROPOFF':
      return 'Driving to destination';
    case 'AWAITING_COMPLETION':
      return 'Delivery done, wrapping up';
    default:
      return 'Trip in progress';
  }
}

export function statusToHuman(status?: string | null): string {
  if (!status) return 'In progress';

  switch (status) {
    case 'LOADING':
      return 'Loading';
    case 'UNLOADING':
      return 'Unloading';
    case 'ACCEPTED':
      return 'Accepted';
    case 'PICKED_UP':
      return 'Picked up';
    case 'ENROUTE':
      return 'En route';
    case 'DELIVERED':
      return 'Delivered';
    case 'SCHEDULED':
      return 'Scheduled';
    case 'PENDING':
      return 'Pending';
    case 'ASSIGNED':
      return 'Assigned';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
    case 'CANCELLED_BY_SYSTEM':
      return 'Cancelled';
    case 'EXPIRED':
      return 'Expired';
    case 'NO_DRIVER_FOUND':
      return 'No driver found';
    default:
      /* Unknown states must stay readable rather than shouting an enum. */
      return status.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
  }
}

/** Prefer the richer phase label; fall back to raw status when it is UNKNOWN. */
export function tripHeadline(
  phase: TripExperiencePhase | undefined,
  status: string | null | undefined,
): string {
  if (phase && phase !== 'UNKNOWN') return phaseToLabel(phase);
  return statusToHuman(status);
}

export function formatEta(etaSeconds?: number | null): string | null {
  if (!etaSeconds || etaSeconds <= 0) return null;

  const minutes = Math.round(etaSeconds / 60);
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder === 0 ? `${hours} hr` : `${hours} hr ${remainder} min`;
}

export function formatScheduledAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Scheduled';

  const day = date.toLocaleDateString('en-ZA', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const time = date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });

  return `${day} · ${time}`;
}

/** "Mdantsane → KuGompo City", or null when either end is missing. */
export function formatRoute(
  pickup?: string | null,
  dropoff?: string | null,
): string | null {
  if (!pickup || !dropoff) return null;
  return `${pickup} → ${dropoff}`;
}

export function isCancelled(status: BookingStatus | string): boolean {
  return status === 'CANCELLED' || status === 'CANCELLED_BY_SYSTEM';
}
